/**
 * Intel Search Component
 * Search through scouting reports to find intel on opponents
 * 🔍 Peck Pirates Intelligence Database
 */

import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import type { ScoutingReportDocument, EventVodDocument } from '@schemas/firestore';

// Game options
const GAMES = [
    { id: 'all', name: 'All Games' },
    { id: 'smash-bros-solo', name: 'Smash Bros Solo' },
    { id: 'smash-bros-crews', name: 'Smash Bros Crews' },
    { id: 'rocket-league', name: 'Rocket League' },
    { id: 'mario-kart', name: 'Mario Kart' },
    { id: 'madden', name: 'Madden NFL' },
    { id: 'fortnite', name: 'Fortnite' },
];

interface ReportWithId extends ScoutingReportDocument {
    id: string;
}

interface VodWithId extends EventVodDocument {
    id: string;
}

interface IntelSearchProps {
    isOpen: boolean;
    onClose: () => void;
}

// Combined intel for an opponent
interface OpponentIntel {
    opponentName: string;
    opponentTeam?: string;
    reports: ReportWithId[];
    vods: VodWithId[];
}

export default function IntelSearch({ isOpen, onClose }: IntelSearchProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [gameFilter, setGameFilter] = useState('all');
    const [reports, setReports] = useState<ReportWithId[]>([]);
    const [vods, setVods] = useState<VodWithId[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedReport, setExpandedReport] = useState<string | null>(null);

    // Load all reports and VoDs
    useEffect(() => {
        async function loadData() {
            if (!isOpen) return;

            setLoading(true);
            try {
                // Load scouting reports
                const reportsSnap = await getDocs(
                    query(collection(db, 'scoutingReports'), orderBy('createdAt', 'desc'))
                );
                const reportsData = reportsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                } as ReportWithId));
                setReports(reportsData);

                // Load opponent VoDs
                const vodsSnap = await getDocs(
                    query(collection(db, 'eventVods'), orderBy('createdAt', 'desc'))
                );
                const vodsData = vodsSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() } as VodWithId))
                    .filter(v => v.associationType === 'opponent' && v.opponentName);
                setVods(vodsData);

            } catch (err) {
                console.error('Error loading intel:', err);
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [isOpen]);

    // Filter reports
    const filteredReports = reports.filter(report => {
        const matchesGame = gameFilter === 'all' || report.game === gameFilter;

        if (!searchQuery.trim()) return matchesGame;

        const q = searchQuery.toLowerCase();
        const matchesSearch =
            report.opponentName.toLowerCase().includes(q) ||
            report.opponentTeam?.toLowerCase().includes(q) ||
            report.strategies.toLowerCase().includes(q) ||
            report.playerNames?.some(p => p.toLowerCase().includes(q)) ||
            report.tags?.some(t => t.toLowerCase().includes(q));

        return matchesGame && matchesSearch;
    });

    // Filter VoDs  
    const filteredVods = vods.filter(vod => {
        const matchesGame = gameFilter === 'all' || vod.game === gameFilter;

        if (!searchQuery.trim()) return matchesGame;

        const q = searchQuery.toLowerCase();
        const matchesSearch =
            vod.opponentName?.toLowerCase().includes(q) ||
            vod.opponentTeam?.toLowerCase().includes(q) ||
            vod.title.toLowerCase().includes(q);

        return matchesGame && matchesSearch;
    });

    // Group by opponent - combining reports and VoDs
    const opponentIntel = new Map<string, OpponentIntel>();

    filteredReports.forEach(report => {
        const key = report.opponentName.toLowerCase();
        if (!opponentIntel.has(key)) {
            opponentIntel.set(key, {
                opponentName: report.opponentName,
                opponentTeam: report.opponentTeam,
                reports: [],
                vods: [],
            });
        }
        opponentIntel.get(key)!.reports.push(report);
    });

    filteredVods.forEach(vod => {
        if (!vod.opponentName) return;
        const key = vod.opponentName.toLowerCase();
        if (!opponentIntel.has(key)) {
            opponentIntel.set(key, {
                opponentName: vod.opponentName,
                opponentTeam: vod.opponentTeam,
                reports: [],
                vods: [],
            });
        }
        opponentIntel.get(key)!.vods.push(vod);
    });

    const groupedIntel = Array.from(opponentIntel.values());

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden bg-secondary-900 rounded-xl border border-secondary-700 shadow-2xl flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-yellow-900/40 via-secondary-900 to-yellow-900/40 border-b border-yellow-500/30 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">🔍</span>
                            <div>
                                <h2 className="text-lg font-bold text-white">Intel Database</h2>
                                <p className="text-xs text-yellow-400 uppercase tracking-wider">
                                    Classified Opponent Intelligence
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-secondary-400 hover:text-white hover:bg-secondary-800 rounded-lg transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Search & Filters */}
                    <div className="flex gap-3">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search opponents, players, tags..."
                                className="w-full pl-10 pr-4 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-yellow-500"
                            />
                            <svg
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        <select
                            value={gameFilter}
                            onChange={(e) => setGameFilter(e.target.value)}
                            className="px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                        >
                            {GAMES.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Results */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : groupedIntel.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-secondary-400">No intel found.</p>
                            {searchQuery && (
                                <p className="text-secondary-500 text-sm mt-1">
                                    Try a different search term.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Stats Bar */}
                            <div className="flex gap-4 text-sm text-secondary-400">
                                <span>{filteredReports.length} report{filteredReports.length !== 1 ? 's' : ''}</span>
                                <span>•</span>
                                <span>{filteredVods.length} VoD{filteredVods.length !== 1 ? 's' : ''}</span>
                                <span>•</span>
                                <span>{groupedIntel.length} opponent{groupedIntel.length !== 1 ? 's' : ''}</span>
                            </div>

                            {/* Intel by Opponent */}
                            {groupedIntel.map((intel) => (
                                <div key={intel.opponentName} className="bg-secondary-800/50 rounded-lg border border-secondary-700 overflow-hidden">
                                    {/* Opponent Header */}
                                    <div className="p-3 bg-secondary-800 border-b border-secondary-700">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-semibold text-white">{intel.opponentName}</h3>
                                                {intel.opponentTeam && (
                                                    <p className="text-sm text-secondary-400">
                                                        {intel.opponentTeam}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                {intel.reports.length > 0 && (
                                                    <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                                                        📝 {intel.reports.length}
                                                    </span>
                                                )}
                                                {intel.vods.length > 0 && (
                                                    <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                                                        📹 {intel.vods.length}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Opponent VoDs */}
                                    {intel.vods.length > 0 && (
                                        <div className="p-3 border-b border-secondary-700 bg-purple-500/5">
                                            <p className="text-xs font-semibold text-purple-400 uppercase mb-2">📹 VoDs</p>
                                            <div className="space-y-1">
                                                {intel.vods.map(vod => (
                                                    <a
                                                        key={vod.id}
                                                        href={vod.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center justify-between p-2 bg-secondary-800/50 rounded hover:bg-secondary-800 transition-colors"
                                                    >
                                                        <span className="text-sm text-white">{vod.title}</span>
                                                        <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                        </svg>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Scouting Reports */}
                                    <div className="divide-y divide-secondary-700">
                                        {intel.reports.map(report => (
                                            <div key={report.id} className="p-3">
                                                <button
                                                    onClick={() => setExpandedReport(
                                                        expandedReport === report.id ? null : report.id
                                                    )}
                                                    className="w-full text-left"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs px-2 py-0.5 bg-primary-500/20 text-primary-400 rounded">
                                                                {GAMES.find(g => g.id === report.game)?.name || report.game}
                                                            </span>
                                                            {report.vodUrl && (
                                                                <span className="text-purple-400">📹</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-secondary-500">
                                                                by {report.createdByName}
                                                            </span>
                                                            <svg
                                                                className={`w-4 h-4 text-secondary-400 transition-transform ${expandedReport === report.id ? 'rotate-180' : ''}`}
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </button>

                                                {/* Expanded Details */}
                                                {expandedReport === report.id && (
                                                    <div className="mt-3 pt-3 border-t border-secondary-700 space-y-3">
                                                        {/* Strategies */}
                                                        <div>
                                                            <p className="text-xs font-semibold text-secondary-400 uppercase mb-1">Strategies</p>
                                                            <p className="text-sm text-white whitespace-pre-wrap">{report.strategies}</p>
                                                        </div>

                                                        {/* Strengths & Weaknesses */}
                                                        <div className="grid sm:grid-cols-2 gap-3">
                                                            {report.strengths && (
                                                                <div>
                                                                    <p className="text-xs font-semibold text-green-400 mb-1">💪 Strengths</p>
                                                                    <p className="text-sm text-secondary-300">{report.strengths}</p>
                                                                </div>
                                                            )}
                                                            {report.weaknesses && (
                                                                <div>
                                                                    <p className="text-xs font-semibold text-red-400 mb-1">🎯 Weaknesses</p>
                                                                    <p className="text-sm text-secondary-300">{report.weaknesses}</p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Tags */}
                                                        {report.tags && report.tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-1">
                                                                {report.tags.map((tag, i) => (
                                                                    <span key={i} className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                                                                        #{tag}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* VoD Link from Report */}
                                                        {report.vodUrl && (
                                                            <a
                                                                href={report.vodUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300"
                                                            >
                                                                📹 Watch VoD
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
